import { useTranslation } from 'react-i18next';
import type { CountryEntry, UsStateEntry } from '@scio3/shared-types';
import { COUNTRIES, US_STATES } from '@/features/settings/utils/profile-constants';
import { SearchableCombobox } from './SearchableCombobox';

/**
 * Searchable Country / US State pickers for address forms (#2332). ~250 countries in a
 * scroll-only Select meant scrolling past most of the alphabet; these filter as you type.
 *
 * Both store the same value the plain Selects they replace stored, so swapping one in does not
 * change what a form saves:
 *   - Country → ISO code (`US`, `DE`).
 *   - State   → `valueKind` picks it, because the forms already disagree: Order Devices stores the
 *     abbreviation (`TX`), Billing Address stores the full name (`Texas`).
 *
 * `testIdPrefix` keeps the testids the Selects had: `{prefix}-trigger` on the trigger and
 * `{prefix}-option-{code}` per option (always the code / abbreviation, whatever `valueKind` is).
 */
type CountryComboboxProps = {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  testIdPrefix?: string;
  className?: string;
};

const countryId = (c: CountryEntry) => c.code;
const countryLabel = (c: CountryEntry) => c.name;

export function CountryCombobox({
  value,
  onChange,
  id,
  testIdPrefix,
  className,
}: CountryComboboxProps) {
  const { t } = useTranslation();
  return (
    <SearchableCombobox
      id={id}
      value={value}
      items={COUNTRIES}
      getId={countryId}
      getLabel={countryLabel}
      onChange={onChange}
      placeholder={t('Select country')}
      searchPlaceholder={t('Search country...')}
      emptyMessage={t('No country found')}
      className={className}
      data-testid={testIdPrefix ? `${testIdPrefix}-trigger` : undefined}
      getItemTestId={testIdPrefix ? (c) => `${testIdPrefix}-option-${c.code}` : undefined}
    />
  );
}

type UsStateComboboxProps = {
  value: string;
  onChange: (value: string) => void;
  /** What the form stores: the 2-letter `abbreviation` or the full `name`. */
  valueKind: 'abbreviation' | 'name';
  id?: string;
  testIdPrefix?: string;
  className?: string;
};

const stateLabel = (s: UsStateEntry) => s.name;

export function UsStateCombobox({
  value,
  onChange,
  valueKind,
  id,
  testIdPrefix,
  className,
}: UsStateComboboxProps) {
  const { t } = useTranslation();
  return (
    <SearchableCombobox
      id={id}
      value={value}
      items={US_STATES}
      getId={(s) => (valueKind === 'abbreviation' ? s.abbreviation : s.name)}
      getLabel={stateLabel}
      onChange={onChange}
      placeholder={t('Select State')}
      searchPlaceholder={t('Search state...')}
      emptyMessage={t('No state found')}
      className={className}
      data-testid={testIdPrefix ? `${testIdPrefix}-trigger` : undefined}
      getItemTestId={testIdPrefix ? (s) => `${testIdPrefix}-option-${s.abbreviation}` : undefined}
    />
  );
}
